import { useEffect, useMemo } from 'react'
import { Box, Input } from '@grupoboticario/flora-react'
import { useTranslation } from 'react-i18next'
import { FormikProps } from 'formik'

import Signature from './Signature'
import withForm from '../hocs/withForm'
import schemaDriver, { Driver } from '../config/schemaDriver'
import { toDocumentValue } from '../utils/cpf'

const previousFieldsAreValid = (values: Driver) => {
  try {
    schemaDriver.validateSyncAt('driverName', values)
    schemaDriver.validateSyncAt('licensePlate', values)
    schemaDriver.validateSyncAt('driverDocument', values)
    return true
  } catch {
    return false
  }
}

const lengthHint = (error: string | undefined, max: number) => (error === 'fieldMinLength' ? 3 : max)

const DriverFields = ({ getFieldProps, errors, touched, values, handleChange, setFieldValue }: FormikProps<Driver>) => {
  const { t } = useTranslation()
  const documentField = getFieldProps('driverDocument')
  const canSign = useMemo(
    () => previousFieldsAreValid(values),
    [values.driverName, values.licensePlate, values.driverDocument]
  )

  useEffect(() => {
    if (!canSign && values.driverSignature) {
      setFieldValue('driverSignature', '')
    }
  }, [canSign, setFieldValue, values.driverSignature])

  return (
    <>
      <Box>
        <Input
          id="driver-name"
          labelText={t('driverName')}
          optionalText=""
          invalidText={t(errors.driverName || 'requiredField', {
            field: t('driverName'),
            number: lengthHint(errors.driverName, 50)
          })}
          invalid={!!errors.driverName && !!touched.driverName}
          {...getFieldProps('driverName')}
        />
      </Box>

      <Box>
        <Input
          id="license-plate"
          labelText={t('licensePlate')}
          optionalText=""
          invalidText={t(errors.licensePlate || 'requiredField', {
            field: t('licensePlate'),
            number: lengthHint(errors.licensePlate, 8)
          })}
          invalid={!!errors.licensePlate && !!touched.licensePlate}
          {...getFieldProps('licensePlate')}
        />
      </Box>

      <Box>
        <Input
          id="driver-document"
          labelText={t('driverDocument')}
          optionalText=""
          inputMode="numeric"
          invalidText={t(errors.driverDocument || 'requiredField', { field: t('driverDocument') })}
          invalid={!!errors.driverDocument && !!touched.driverDocument}
          {...documentField}
          onChange={(event) => setFieldValue('driverDocument', toDocumentValue(event.target.value))}
        />
      </Box>

      <Box>
        <Signature
          id="driver-signature"
          label={t('driverSignature')}
          onChange={handleChange('driverSignature')}
          hasError={!!errors.driverSignature && !!touched.driverSignature}
          disabled={!canSign}
          signature={values.driverSignature}
        />
      </Box>
    </>
  )
}

const FormDriver = withForm(DriverFields)

export default FormDriver
